import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Rocket,
  ArrowRight,
  Cloud,
  Send,
  Zap,
  Globe,
  GitBranch,
  BarChart3,
  Code2,
  CheckCircle2,
  Check,
  Twitter,
  Github,
  Linkedin,
} from "lucide-react";
import Logo from "../components/app/Logo";
import { pagesByColumn } from "../lib/staticPages";
import { api, formatApiErrorDetail } from "../lib/api";

/* ============================================================
   Marquee strip
   ============================================================ */
const MARQUEE_ITEMS = [
  "AZURE",
  "AWS",
  "GCP",
  "17+ ARCHITECTURE TEMPLATES",
  "AI-POWERED",
  "REAL COST ESTIMATES",
  "TERRAFORM IaC EXPORT",
];

const Marquee = () => (
  <div className="landing-marquee" data-testid="marquee-strip">
    <div className="landing-marquee-track">
      {[0, 1].map((copy) => (
        <div key={copy} className="landing-marquee-group" aria-hidden={copy === 1 ? "true" : undefined}>
          {MARQUEE_ITEMS.map((label) => (
            <div key={`${copy}-${label}`} className="landing-marquee-item">
              <span className="font-mono whitespace-nowrap">{label}</span>
              <span className="landing-marquee-dot" aria-hidden="true" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================
   Step 01 — chat interface (interactive)
   ============================================================ */
const ChatStep = () => {
  const [messages, setMessages] = useState([
    {
      from: "ai",
      text:
        "Hello! Tell me about the application you want to build. What are your scale expectations and tech preferences?",
    },
    {
      from: "user",
      text:
        "I need a scalable REST API for ~50k daily users. PostgreSQL for primary DB, Redis for caching, with a separate worker queue for async jobs…",
    },
  ]);
  const [analyzing, setAnalyzing] = useState(true);
  const [input, setInput] = useState("");
  const dots = useDots(analyzing);

  useEffect(() => {
    const t = setTimeout(() => setAnalyzing(false), 3200);
    return () => clearTimeout(t);
  }, []);

  const send = () => {
    if (!input.trim()) return;
    setMessages((m) => [...m, { from: "user", text: input.trim() }]);
    setInput("");
    setAnalyzing(true);
    setTimeout(() => {
      setMessages((m) => [
        ...m,
        {
          from: "ai",
          text:
            "Got it. I'll provision an auto-scaling API tier, managed PostgreSQL (Flexible Server), Redis cache, and a Service Bus queue for workers. Continue to step 2 to pick a provider.",
        },
      ]);
      setAnalyzing(false);
    }, 1400);
  };

  return (
    <div
      data-testid="step1-chat"
      className="rounded-[14px] border overflow-hidden"
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
        boxShadow: "0 30px 80px -30px rgba(45,212,191,0.15)",
      }}
    >
      {/* Window chrome */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: "var(--color-border-secondary)", background: "var(--color-background-tertiary)" }}
      >
        <div className="flex items-center gap-1.5">
          <span style={{ width: 11, height: 11, borderRadius: 9999, background: "#ef4444" }} />
          <span style={{ width: 11, height: 11, borderRadius: 9999, background: "#facc15" }} />
          <span style={{ width: 11, height: 11, borderRadius: 9999, background: "#22c55e" }} />
        </div>
        <span className="font-mono text-text-tertiary" style={{ fontSize: 11, letterSpacing: "0.04em" }}>
          guided-sentry · AI Architect
        </span>
        <span style={{ width: 33 }} />
      </div>
      {/* Conversation */}
      <div className="p-5 space-y-3" style={{ minHeight: 280 }}>
        {messages.map((m, i) => (
          <Bubble key={i} from={m.from} text={m.text} />
        ))}
        {analyzing && (
          <Bubble from="ai" text={`Analyzing your requirements${dots}`} dim />
        )}
      </div>
      {/* Composer */}
      <div className="p-4 border-t" style={{ borderColor: "var(--color-border-secondary)" }}>
        <div
          className="flex items-center gap-2 rounded-[10px] border"
          style={{ borderColor: "var(--color-border-primary)", background: "var(--color-background-tertiary)" }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            data-testid="step1-chat-input"
            placeholder="Describe your infrastructure needs…"
            className="flex-1 bg-transparent outline-none px-4"
            style={{ height: 44, fontSize: 13, color: "var(--color-text-primary)" }}
          />
          <button
            onClick={send}
            data-testid="step1-chat-send"
            className="motion-micro grid place-items-center mr-1.5"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "var(--color-accent)",
              color: "#0a0a0a",
              border: "none",
              cursor: "pointer",
            }}
            aria-label="Send"
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

const Bubble = ({ from, text, dim }) => {
  const isAI = from === "ai";
  return (
    <div className={`flex gap-2.5 ${isAI ? "" : "flex-row-reverse"}`}>
      <span
        className="grid place-items-center rounded-full font-mono shrink-0"
        style={{
          width: 28,
          height: 28,
          fontSize: 10,
          fontWeight: 600,
          background: isAI ? "var(--color-accent-subtle)" : "rgba(255,255,255,0.06)",
          color: isAI ? "var(--color-accent)" : "var(--color-text-secondary)",
          border: `1px solid ${isAI ? "var(--color-accent)" : "var(--color-border-primary)"}`,
        }}
      >
        GS
      </span>
      <div
        className="rounded-[10px] px-3.5 py-2.5"
        style={{
          maxWidth: "82%",
          background: isAI
            ? "var(--color-background-tertiary)"
            : "var(--color-accent-subtle)",
          border: `1px solid ${isAI ? "var(--color-border-secondary)" : "rgba(45,212,191,0.3)"}`,
          color: isAI ? "var(--color-text-secondary)" : "var(--color-text-primary)",
          fontSize: 13,
          lineHeight: 1.55,
          opacity: dim ? 0.7 : 1,
        }}
      >
        {text}
      </div>
    </div>
  );
};

const useDots = (active) => {
  const [n, setN] = useState(1);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setN((v) => (v % 3) + 1), 400);
    return () => clearInterval(t);
  }, [active]);
  return ".".repeat(n);
};

/* ============================================================
   Step 02 — provider picker + budget slider (interactive)
   ============================================================ */
const PROVIDERS = [
  { id: "azure", label: "Azure", sub: "Microsoft", color: "#3b82f6", icon: Cloud },
  { id: "aws", label: "AWS", sub: "Amazon", color: "#f59e0b", icon: Zap },
  { id: "gcp", label: "GCP", sub: "Google", color: "#ef4444", icon: Globe },
];

const BudgetStep = () => {
  const [provider, setProvider] = useState("aws");
  const [budget, setBudget] = useState(2500);
  const meta = PROVIDERS.find((p) => p.id === provider);

  return (
    <div
      data-testid="step2-budget"
      className="rounded-[14px] border p-6"
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
        boxShadow: "0 30px 80px -30px rgba(45,212,191,0.15)",
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <span
          className="font-mono text-text-tertiary"
          style={{ fontSize: 10, letterSpacing: "0.14em" }}
        >
          SELECT CLOUD PROVIDER
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-7">
        {PROVIDERS.map((p) => {
          const Icon = p.icon;
          const active = provider === p.id;
          return (
            <button
              key={p.id}
              data-testid={`step2-provider-${p.id}`}
              onClick={() => setProvider(p.id)}
              className="motion-canvas"
              style={{
                background: active
                  ? `${p.color}1A`
                  : "var(--color-background-tertiary)",
                border: `1.5px solid ${active ? p.color : "var(--color-border-primary)"}`,
                borderRadius: 12,
                padding: "16px 12px",
                cursor: "pointer",
                textAlign: "center",
                boxShadow: active ? `0 12px 30px -12px ${p.color}66` : "none",
              }}
            >
              <span
                className="grid place-items-center rounded-full mx-auto mb-3"
                style={{
                  width: 36,
                  height: 36,
                  background: active ? `${p.color}33` : "var(--color-background-secondary)",
                  border: `1px solid ${active ? p.color : "var(--color-border-secondary)"}`,
                }}
              >
                <Icon size={16} color={p.color} />
              </span>
              <div
                className="text-text-primary"
                style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}
              >
                {p.label}
              </div>
              <span
                className="inline-block mt-2 font-mono rounded-full"
                style={{
                  padding: "2px 8px",
                  fontSize: 9,
                  letterSpacing: "0.08em",
                  background: `${p.color}1A`,
                  color: p.color,
                  border: `1px solid ${p.color}66`,
                }}
              >
                {p.sub}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-3">
        <span
          className="font-mono text-text-tertiary"
          style={{ fontSize: 10, letterSpacing: "0.14em" }}
        >
          MONTHLY BUDGET
        </span>
        <span
          className="font-mono text-text-primary"
          style={{ fontSize: 18, fontWeight: 600 }}
          data-testid="step2-budget-value"
        >
          ${budget.toLocaleString()} <span className="text-text-tertiary" style={{ fontSize: 12, fontWeight: 400 }}>/ mo</span>
        </span>
      </div>
      <input
        type="range"
        min={500}
        max={5000}
        step={50}
        value={budget}
        onChange={(e) => setBudget(Number(e.target.value))}
        data-testid="step2-budget-slider"
        className="w-full"
        style={{
          accentColor: "var(--color-accent)",
          height: 4,
        }}
      />
      <div className="flex justify-between mt-1.5 font-mono text-text-tertiary" style={{ fontSize: 10 }}>
        <span>$500</span>
        <span>$5,000+</span>
      </div>

      <div
        className="mt-5 rounded-[10px] flex items-center gap-2 px-4"
        style={{
          background: "var(--color-accent-subtle)",
          border: "1px solid rgba(45,212,191,0.3)",
          height: 44,
        }}
        data-testid="step2-summary"
      >
        <CheckCircle2 size={14} color="var(--color-accent)" />
        <span className="text-text-primary" style={{ fontSize: 12 }}>
          {meta.label} · ${budget.toLocaleString()}/mo · Production tier selected
        </span>
      </div>
    </div>
  );
};

/* ============================================================
   Step 03 — deliverables (animated progress)
   ============================================================ */
const DELIVERABLES = [
  { id: "diagram", icon: GitBranch, title: "Architecture Diagram", sub: "Interactive node graph", tag: "SVG + PNG" },
  { id: "cost", icon: BarChart3, title: "Cost Estimate", sub: "Per-service breakdown", tag: "Real pricing" },
  { id: "tf", icon: Code2, title: "Terraform IaC", sub: "Review-required HCL", tag: ".tf export" },
  { id: "bicep", icon: Cloud, title: "Bicep Export", sub: "Azure-native templates", tag: "bicep export" },
];

const DeliverablesStep = () => {
  const [pct, setPct] = useState(0);
  const ref = useRef(null);
  // "Have we already run the count-up?" as a ref rather than by reading `pct`.
  // The old guard read `pct` from a stale closure while declaring `[]` deps,
  // which is what react-hooks/exhaustive-deps was flagging; a ref is the honest
  // form of "run once", and it doesn't need to be a dependency.
  const animatedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !animatedRef.current) {
          animatedRef.current = true;
          // animate to 100
          let v = 0;
          const tick = () => {
            v = Math.min(100, v + 2);
            setPct(v);
            if (v < 100) requestAnimationFrame(tick);
          };
          tick();
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid="step3-deliverables"
      className="rounded-[14px] border p-6"
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
        boxShadow: "0 30px 80px -30px rgba(45,212,191,0.15)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <span
          className="font-mono text-text-tertiary"
          style={{ fontSize: 10, letterSpacing: "0.14em" }}
        >
          YOUR DELIVERABLES
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {DELIVERABLES.map((d) => {
          const Icon = d.icon;
          return (
            <div
              key={d.id}
              data-testid={`step3-${d.id}`}
              className="rounded-[10px] border p-4 motion-panel"
              style={{
                background: "var(--color-background-tertiary)",
                borderColor: "var(--color-border-primary)",
              }}
            >
              <span
                className="grid place-items-center rounded-[8px] mb-3"
                style={{
                  width: 32,
                  height: 32,
                  background: "var(--color-accent-subtle)",
                  border: "1px solid rgba(45,212,191,0.3)",
                }}
              >
                <Icon size={14} color="var(--color-accent)" />
              </span>
              <p className="text-text-primary" style={{ fontSize: 13, fontWeight: 600 }}>
                {d.title}
              </p>
              <p className="text-text-secondary" style={{ fontSize: 11 }}>
                {d.sub}
              </p>
              <span
                className="inline-block mt-2 font-mono rounded-md"
                style={{
                  padding: "2px 8px",
                  fontSize: 9,
                  letterSpacing: "0.08em",
                  background: "var(--color-accent-subtle)",
                  color: "var(--color-accent)",
                  border: "1px solid rgba(45,212,191,0.35)",
                }}
              >
                {d.tag}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-text-tertiary" style={{ fontSize: 12 }}>
            Generating your package…
          </span>
          <span className="font-mono text-text-primary" style={{ fontSize: 12, fontWeight: 600 }} data-testid="step3-pct">
            {pct}%
          </span>
        </div>
        <div
          style={{
            height: 4,
            borderRadius: 9999,
            background: "var(--color-background-tertiary)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "var(--color-accent)",
              transition: "width 60ms linear",
            }}
          />
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   Generic step row
   ============================================================ */
const StepRow = ({ n, kicker, title, body, chips, cta, mock, reverse }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
    <div>
      <div className="flex items-baseline gap-5 mb-6">
        <span
          className="font-bold"
          style={{
            fontSize: 88,
            lineHeight: 1,
            color: "var(--color-accent)",
            opacity: 0.18,
            letterSpacing: "-0.04em",
            fontFamily: "Inter, system-ui",
          }}
        >
          {n}
        </span>
        <span style={{ width: 1, alignSelf: "stretch", background: "var(--color-accent)", opacity: 0.4 }} />
        <span
          className="font-mono"
          style={{ fontSize: 11, letterSpacing: "0.18em", color: "var(--color-accent)" }}
        >
          {kicker}
        </span>
      </div>
      <h3
        className="text-text-primary mb-4"
        style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
      >
        {title}
      </h3>
      <p className="text-text-secondary mb-6" style={{ fontSize: 15, lineHeight: 1.65, maxWidth: 520 }}>
        {body}
      </p>
      {chips && (
        <div className="flex flex-wrap gap-2 mb-6">
          {chips.map((c) => (
            <span
              key={c}
              className="font-mono rounded-md"
              style={{
                padding: "5px 10px",
                fontSize: 11,
                color: "var(--color-text-secondary)",
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-border-primary)",
              }}
            >
              {c}
            </span>
          ))}
        </div>
      )}
      {cta}
    </div>
    <div>{mock}</div>
  </div>
);

/* ============================================================
   Final completed list (Step 03 chips)
   ============================================================ */
const FinalChecklist = () => {
  const items = [
    "Interactive architecture diagrams",
    "Real cost breakdowns by service",
    "Terraform IaC export",
    "Bicep export for Azure deployments",
  ];
  return (
    <ul className="space-y-2 mb-6">
      {items.map((t) => (
        <li key={t} className="flex items-center gap-2 text-text-secondary" style={{ fontSize: 13 }}>
          <Check size={14} color="var(--color-accent)" /> {t}
        </li>
      ))}
    </ul>
  );
};

/* ============================================================
   Footer
   ============================================================ */
const Footer = () => (
  <footer
    className="border-t"
    style={{ borderColor: "var(--color-border-primary)", background: "var(--color-background-primary)" }}
  >
    <div className="max-w-[1200px] mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-5 gap-10">
      <div className="md:col-span-2">
        <Logo />
        <p className="text-text-secondary mt-4" style={{ fontSize: 13, lineHeight: 1.65, maxWidth: 320 }}>
          Plan your cloud before you build it. AI-powered architecture, real cost estimates, and IaC exports.
        </p>
      </div>
      <FooterCol title="PRODUCT" items={pagesByColumn("product")} />
      <FooterCol title="RESOURCES" items={pagesByColumn("resources")} />
      <FooterCol title="COMPANY" items={pagesByColumn("company")} />
    </div>
    <div className="border-t" style={{ borderColor: "var(--color-border-secondary)" }}>
      <div className="max-w-[1200px] mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-4">
        <span className="font-mono text-text-tertiary" style={{ fontSize: 11, letterSpacing: "0.04em" }}>
          © 2026 Guided Sentry. All rights reserved.
        </span>
        <div className="flex items-center gap-5 text-text-tertiary">
          <a href="#" data-testid="footer-twitter" className="motion-micro hover:text-accent" style={{ color: "inherit" }}>
            <Twitter size={14} />
          </a>
          <a href="#" data-testid="footer-github" className="motion-micro hover:text-accent" style={{ color: "inherit" }}>
            <Github size={14} />
          </a>
          <a href="#" data-testid="footer-linkedin" className="motion-micro hover:text-accent" style={{ color: "inherit" }}>
            <Linkedin size={14} />
          </a>
        </div>
        <div className="flex items-center gap-5">
          <Link
            to="/privacy"
            data-testid="footer-privacy-link"
            className="text-text-tertiary motion-micro"
            style={{ fontSize: 12, textDecoration: "none" }}
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            data-testid="footer-terms-link"
            className="text-text-tertiary motion-micro"
            style={{ fontSize: 12, textDecoration: "none" }}
          >
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

const FooterCol = ({ title, items }) => (
  <div>
    <p
      className="font-mono mb-4"
      style={{ fontSize: 11, letterSpacing: "0.14em", color: "var(--color-accent)" }}
    >
      {title}
    </p>
    <ul className="space-y-2.5">
      {items.map((i) => (
        <li key={i.path}>
          <Link
            to={i.path}
            data-testid={`footer-link-${i.column}-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="text-text-secondary motion-micro hover:text-text-primary"
            style={{ fontSize: 13, textDecoration: "none" }}
          >
            {i.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const EMPTY_EARLY_ACCESS_FORM = {
  name: "",
  email: "",
  job_title: "",
  referral_source: "",
  intended_use: "",
  company_website: "",
};

const EarlyAccessDialog = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY_EARLY_ACCESS_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_EARLY_ACCESS_FORM);
      setSubmitting(false);
      setSubmitted(false);
      setError("");
      return undefined;
    }

    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open, onClose]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/early-access", form);
      setSubmitted(true);
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail) || "We couldn't save your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="early-access-backdrop" onMouseDown={onClose}>
      <section
        className="early-access-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="early-access-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="early-access-close" aria-label="Close early access form" onClick={onClose}>
          ×
        </button>
        {submitted ? (
          <div className="early-access-success">
            <CheckCircle2 size={30} aria-hidden="true" />
            <h2 id="early-access-title">You&apos;re on the list.</h2>
            <p>Thanks for your interest in Guided Sentry. We&apos;ll reach out when early access opens.</p>
            <button type="button" className="early-access-submit" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <p className="early-access-eyebrow">EARLY ACCESS</p>
            <h2 id="early-access-title">Help shape Guided Sentry.</h2>
            <p className="early-access-intro">Tell us a little about yourself and how you&apos;d use the tool.</p>
            <form className="early-access-form" onSubmit={submit} data-testid="early-access-form">
              <label>
                <span>Full name</span>
                <input name="name" value={form.name} onChange={updateField} autoComplete="name" required />
              </label>
              <label>
                <span>Email</span>
                <input name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" required />
              </label>
              <label>
                <span>Job title</span>
                <input name="job_title" value={form.job_title} onChange={updateField} autoComplete="organization-title" required />
              </label>
              <label>
                <span>How did you hear about us?</span>
                <input name="referral_source" value={form.referral_source} onChange={updateField} required />
              </label>
              <label>
                <span>How will you use Guided Sentry?</span>
                <textarea name="intended_use" value={form.intended_use} onChange={updateField} minLength={10} required />
              </label>
              <label className="early-access-honeypot" aria-hidden="true">
                <span>Company website</span>
                <input name="company_website" value={form.company_website} onChange={updateField} tabIndex={-1} autoComplete="off" />
              </label>
              {error && <p className="early-access-error" role="alert">{error}</p>}
              <button type="submit" className="early-access-submit" disabled={submitting}>
                {submitting ? "Requesting access…" : "Request early access"}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
};

const EMPTY_DEMO_REQUEST_FORM = {
  name: "",
  company: "",
  email: "",
  team_size: "",
  message: "",
  company_website: "",
};

const EMPTY_CONTACT_MESSAGE_FORM = {
  name: "",
  email: "",
  message: "",
  company_website: "",
};

const LeadDialog = ({ open, onClose, kind }) => {
  const isDemo = kind === "demo";
  const [form, setForm] = useState(isDemo ? EMPTY_DEMO_REQUEST_FORM : EMPTY_CONTACT_MESSAGE_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setForm(isDemo ? EMPTY_DEMO_REQUEST_FORM : EMPTY_CONTACT_MESSAGE_FORM);
      setSubmitting(false);
      setSubmitted(false);
      setError("");
      return undefined;
    }

    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open, onClose, isDemo]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post(isDemo ? "/demo-requests" : "/contact-messages", form);
      setSubmitted(true);
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail) || "We couldn't send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const title = isDemo ? "Book a company demo" : "Contact Guided Sentry";
  const intro = isDemo
    ? "Tell us about your team and we’ll arrange a guided walkthrough."
    : "Send us a message and we’ll get back to you shortly.";

  return (
    <div className="early-access-backdrop" onMouseDown={onClose}>
      <section className="early-access-dialog" role="dialog" aria-modal="true" aria-labelledby={`${kind}-form-title`} onMouseDown={(event) => event.stopPropagation()}>
        <button type="button" className="early-access-close" aria-label={`Close ${isDemo ? "demo" : "contact"} form`} onClick={onClose}>×</button>
        {submitted ? (
          <div className="early-access-success">
            <CheckCircle2 size={30} aria-hidden="true" />
            <h2 id={`${kind}-form-title`}>{isDemo ? "Demo request received." : "Message received."}</h2>
            <p>{isDemo ? "Thanks for your interest. We’ll be in touch to schedule your walkthrough." : "Thanks for reaching out. We’ll be in touch soon."}</p>
            <button type="button" className="early-access-submit" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <p className="early-access-eyebrow">{isDemo ? "COMPANY DEMO" : "CONTACT"}</p>
            <h2 id={`${kind}-form-title`}>{title}</h2>
            <p className="early-access-intro">{intro}</p>
            <form className="early-access-form" onSubmit={submit} data-testid={`${kind}-form`}>
              <label>
                <span>Full name</span>
                <input name="name" value={form.name} onChange={updateField} autoComplete="name" required />
              </label>
              {isDemo && <label>
                <span>Company</span>
                <input name="company" value={form.company} onChange={updateField} autoComplete="organization" required />
              </label>}
              <label>
                <span>Work email</span>
                <input name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" required />
              </label>
              {isDemo && <label>
                <span>Team size</span>
                <select name="team_size" value={form.team_size} onChange={updateField} required>
                  <option value="" disabled>Select team size</option>
                  <option>1–10</option><option>11–50</option><option>51–200</option><option>201–1,000</option><option>1,001+</option>
                </select>
              </label>}
              <label>
                <span>{isDemo ? "What would you like to explore?" : "Message"}</span>
                <textarea name="message" value={form.message} onChange={updateField} required />
              </label>
              <label className="early-access-honeypot" aria-hidden="true">
                <span>Company website</span>
                <input name="company_website" value={form.company_website} onChange={updateField} tabIndex={-1} autoComplete="off" />
              </label>
              {error && <p className="early-access-error" role="alert">{error}</p>}
              <button type="submit" className="early-access-submit" disabled={submitting}>
                {submitting ? "Sending…" : isDemo ? "Request a demo" : "Send message"}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
};

/* ============================================================
   Landing
   ============================================================ */
function LegacyLanding() {
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary" data-testid="landing-page">
      <div className="landing-hero-stage">
        <img
          src="/solar-hero.png"
          alt=""
          className="landing-hero-visual"
          aria-hidden="true"
        />

        {/* Top nav */}
        <nav className="landing-nav">
          <div className="landing-nav-inner">
            <Logo size={78} />
            <div className="landing-nav-actions">
              <Link
                to="/login"
                data-testid="nav-getstarted-btn"
                className="landing-nav-cta motion-micro"
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-accent-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-accent)")}
              >
                Admin sign in
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="landing-hero">
          <div className="landing-hero-content">
            <div
              className="landing-hero-pill fade-up"
              style={{ animationDelay: "20ms" }}
              data-testid="hero-pill-badge"
            >
              <Cloud size={12} color="var(--color-accent)" />
              <span className="font-mono">Cloud architecture, reimagined</span>
            </div>

            <h1
              className="landing-hero-headline fade-up"
              style={{ animationDelay: "80ms" }}
              data-testid="hero-headline"
            >
              Plan your cloud
              <br />
              before you build it.
            </h1>

            <p
              className="landing-hero-subhead fade-up"
              style={{ animationDelay: "160ms" }}
              data-testid="hero-subhead"
            >
              Guided Sentry generates a complete cloud architecture from a description of what you&apos;re
              building — with cost estimates, provider comparisons, and IaC export. No blank canvas. No
              guessing.
            </p>

            <div className="landing-hero-actions fade-up" style={{ animationDelay: "220ms" }}>
              <button
                type="button"
                data-testid="hero-primary-cta"
                className="landing-hero-primary motion-micro"
                onClick={() => setEarlyAccessOpen(true)}
              >
                Early access
              </button>
              <a href="#how" data-testid="hero-secondary-cta" className="landing-hero-secondary motion-micro">
                See how it works
              </a>
            </div>
          </div>

          {/* Capability strip */}
          <Marquee />
        </section>
      </div>

      {/* How it works */}
      <section id="how" className="landing-how relative">
        <div className="absolute inset-0 side-glow-left pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-20">
            <span
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-7"
              style={{
                background: "var(--color-accent-subtle)",
                border: "1px solid rgba(45,212,191,0.4)",
              }}
            >
              <Rocket size={12} color="var(--color-accent)" />
              <span
                className="font-mono"
                style={{ fontSize: 11, letterSpacing: "0.14em", color: "var(--color-accent)" }}
              >
                HOW IT WORKS
              </span>
            </span>
            <h2
              className="text-text-primary mx-auto"
              style={{
                fontSize: "clamp(32px, 4.5vw, 48px)",
                fontWeight: 700,
                letterSpacing: "-0.025em",
                lineHeight: 1.1,
                maxWidth: 780,
              }}
              data-testid="how-headline"
            >
              From idea to architecture in minutes
            </h2>
            <p className="text-text-secondary mt-5" style={{ fontSize: 15, lineHeight: 1.65 }}>
              Three simple steps to your complete cloud blueprint.
            </p>
          </div>

          <div className="space-y-32">
            <StepRow
              n="01"
              kicker="STEP ONE"
              title="Describe what you're building"
              body="Chat naturally with our AI to describe your application, expected scale, and infrastructure preferences. No blank canvas, no confusing forms – just a conversation that understands what you actually need."
              chips={["Natural language input", "AI-powered understanding", "No setup required"]}
              mock={<ChatStep />}
            />

            <StepRow
              n="02"
              kicker="STEP TWO"
              title="Set your budget and platform"
              body="Choose between Azure, AWS, and Google Cloud — or let us recommend the best fit. Set a monthly budget range and we'll tailor every service selection to keep you within it."
              chips={["Multi-cloud support", "Budget-aware selection", "Cost optimization"]}
              mock={<BudgetStep />}
              reverse
            />

            <StepRow
              n="03"
              kicker="STEP THREE"
              title="Get your complete architecture"
              body="Review your architecture diagram, cost estimates, and Terraform or Bicep drafts together. IaC exports may contain TODO stubs and need validation before deployment."
              cta={
                <>
                  <FinalChecklist />
                  <button
                    type="button"
                    data-testid="how-final-cta"
                    className="inline-flex items-center gap-2 rounded-[8px] motion-micro"
                    style={{
                      background: "var(--color-accent)",
                      color: "#0a0a0a",
                      height: 44,
                      padding: "0 22px",
                      fontSize: 14,
                      fontWeight: 600,
                      textDecoration: "none",
                      boxShadow: "0 14px 36px -14px rgba(45,212,191,0.55)",
                    }}
                    onClick={() => setEarlyAccessOpen(true)}
                  >
                    Request early access <ArrowRight size={14} />
                  </button>
                </>
              }
              mock={<DeliverablesStep />}
            />
          </div>
        </div>
      </section>

      <Footer />
      <EarlyAccessDialog open={earlyAccessOpen} onClose={() => setEarlyAccessOpen(false)} />
    </div>
  );
}

/* Figma redesign: these small, CSS-drawn previews preserve the visual language
   of the source file without shipping a screenshot in place of product UI. */
const StoryScreen = ({ variant }) => {
  if (variant === "architecture") {
    return (
      <div className="gs-screen gs-architecture" aria-label="Architecture diagram preview">
        <div className="gs-screen-bar"><span /><span>Architecture</span><span>Export</span></div>
        <div className="gs-node gs-node-top">CloudFront</div><div className="gs-connector gs-connector-vertical" />
        <div className="gs-node-row"><span className="gs-node">API</span><span className="gs-node">Worker</span><span className="gs-node">Cache</span><span className="gs-node">Queue</span></div>
        <div className="gs-connector gs-connector-wide" /><div className="gs-node gs-node-db">PostgreSQL</div>
      </div>
    );
  }
  if (variant === "comparison") {
    return <div className="gs-screen gs-comparison" aria-label="Provider comparison preview"><div className="gs-spinner">✦</div><strong>Choosing services for the workload</strong><span>Step 4 of 7</span><div className="gs-progress"><i /></div></div>;
  }
  return (
    <div className="gs-screen gs-chat" aria-label="Guided Sentry chat preview">
      <div className="gs-screen-bar"><span>Guided Sentry</span><span>New project</span></div>
      <p>What are you building and what matters most?</p><p className="gs-chat-reply">An API for a growing product, with reliable data and predictable costs.</p><p>Great. Let&apos;s turn that into a cloud architecture.</p><div className="gs-composer">Type a message <b>Send</b></div>
    </div>
  );
};

const StoryBlock = ({ number, title, body, preview, reverse }) => (
  <article className={`gs-story-block ${reverse ? "gs-story-reverse" : ""}`}>
    <div className="gs-story-copy"><h3>{title}</h3><p>{body}</p><span>STEP {number} &nbsp; {number === "01" ? "Input + architecture" : number === "02" ? "Compare providers" : "IaC export"}</span></div>
    <StoryScreen variant={preview} />
  </article>
);

const CAPABILITIES = [
  ["01", "Complete cloud architecture", "Guided Sentry generates a complete architecture from your description, so you can review a full plan before you build."],
  ["02", "Cost estimates and comparisons", "See cost estimates and provider tradeoffs early, so you can make informed decisions before the first resource is created."],
  ["03", "Provider comparisons", "Compare cloud providers side by side and understand the implications of each choice before you commit."],
  ["04", "IaC export", "Move from a cloud architecture plan to infrastructure as code without rebuilding the same decisions by hand."],
];

function ApproximateLanding() {
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);
  return (
    <main className="gs-landing" data-testid="landing-page">
      <div className="gs-backdrop" aria-hidden="true" />
      <nav className="gs-nav">
        <Link to="/" className="gs-wordmark" aria-label="Guided Sentry home"><Logo size={52} /></Link>
        <div className="gs-nav-links"><a href="#product">Product</a><a href="#how">How it works</a><a href="#contact">Contact</a><Link to="/login" className="gs-sign-in">Sign in</Link><button type="button" className="gs-button gs-button-light" onClick={() => setEarlyAccessOpen(true)}>Early access</button></div>
      </nav>
      <section className="gs-hero" id="product">
        <p className="gs-eyebrow">GUIDED SENTRY</p><h1>Plan your cloud<br />before you build it.</h1><p className="gs-hero-copy">Get a complete cloud architecture, cost estimates, and implementation guidance from a simple description of what you&apos;re building.</p>
        <div className="gs-actions"><button type="button" className="gs-button gs-button-light" onClick={() => setEarlyAccessOpen(true)}>Start planning <ArrowRight size={14} /></button><a className="gs-button gs-button-quiet" href="#how">See how it works</a></div>
        <div className="gs-hero-product"><StoryScreen variant="chat" /></div>
      </section>
      <section className="gs-content" id="how">
        <div className="gs-section-heading"><p className="gs-eyebrow">SCROLL STORY</p><h2>From idea to architecture<br />in minutes.</h2></div>
        <StoryBlock number="01" title="Describe what you need" body="Start with a description of the workload, traffic, and compliance requirements. Guided Sentry turns that input into a complete cloud architecture." preview="chat" />
        <StoryBlock number="02" title="Compare providers and costs" body="See provider options side by side, review cost estimates, and understand the tradeoffs before you commit to a specific cloud stack." preview="comparison" reverse />
        <StoryBlock number="03" title="Export infrastructure as code" body="Move from a cloud architecture plan to IaC without starting from a blank canvas or rebuilding the same decisions by hand." preview="architecture" />
        <section className="gs-capabilities"><p className="gs-eyebrow">CAPABILITIES</p><h2>Everything you need to plan with confidence</h2><div className="gs-capability-grid">{CAPABILITIES.map(([number, title, body]) => <article key={number} className="gs-capability"><span>{number}</span><h3>{title}</h3><p>{body}</p></article>)}</div></section>
        <section className="gs-cta" id="contact"><p className="gs-eyebrow">READY TO PLAN</p><h2>See how Guided Sentry works</h2><p>Get a guided walkthrough of the product and explore a clearer way to plan your cloud architecture.</p><div className="gs-actions"><button type="button" className="gs-button gs-button-light" onClick={() => setEarlyAccessOpen(true)}>Book a demo</button><button type="button" className="gs-button gs-button-quiet" onClick={() => setEarlyAccessOpen(true)}>Early access</button></div></section>
      </section>
      <footer className="gs-footer"><div><strong>Guided Sentry</strong><span>Plan your cloud before you build it.</span></div><div><a href="#product">Product</a><a href="#how">How it works</a><a href="#contact">Contact</a></div></footer>
      <EarlyAccessDialog open={earlyAccessOpen} onClose={() => setEarlyAccessOpen(false)} />
    </main>
  );
}

const EXPORTED_STEPS = [
  {
    number: "01", title: "Describe what you need", label: "Input → architecture", image: "/figma/storytelling-image-1.png",
    description: "Start with a description of the workload, traffic, and compliance requirements. Guided Sentry turns that input into a complete cloud architecture.",
  },
  {
    number: "02", title: "Compare providers and costs", label: "Compare providers", image: "/figma/storytelling-image-2.png",
    description: "See provider options side by side, review cost estimates, and understand the tradeoffs before you commit to a specific cloud stack.",
  },
  {
    number: "03", title: "Export infrastructure as code", label: "IaC export", image: "/figma/storytelling-image-3.png",
    description: "Move from a cloud architecture plan to IaC without starting from a blank canvas or rebuilding the same decisions by hand.",
  },
];

const EXPORTED_CAPABILITIES = [
  ["01", "Complete cloud architecture", "Guided Sentry generates a complete architecture from your description, so you can review a full plan before you build."],
  ["02", "Cost estimates and comparisons", "See cost estimates and provider tradeoffs early, so you can make informed decisions before the first resource is created."],
  ["03", "Provider comparisons", "Compare cloud providers side by side and understand the implications of each choice before you commit."],
  ["04", "IaC export", "Move from a cloud architecture plan to infrastructure as code without rebuilding the same decisions by hand."],
];

const FIGMA_SLIDER_ITEMS = [
  "AI-POWERED ARCHITECTURE",
  "REAL COST ESTIMATES",
  "AZURE · AWS · GCP",
  "PROVIDER COMPARISONS",
  "TERRAFORM IaC EXPORT",
];

const FigmaCapabilitySlider = () => (
  <aside className="fx-slider" aria-label="Guided Sentry capabilities">
    <div className="fx-slider-track">
      {[0, 1].map((copy) => (
        <div className="fx-slider-group" key={copy} aria-hidden={copy === 1 ? "true" : undefined}>
          {FIGMA_SLIDER_ITEMS.map((label) => (
            <span className="fx-slider-item" key={`${copy}-${label}`}>
              {label}
            </span>
          ))}
        </div>
      ))}
    </div>
  </aside>
);

const FigmaLanding = ({ onEarlyAccess, onBookDemo, onContact }) => (
  <main className="fx-page" data-testid="landing-page">
    <section className="fx-hero" id="product">
      <img className="fx-hero-background" src="/figma/hero-bg-image.png" alt="" aria-hidden="true" />
      <nav className="fx-nav" aria-label="Main navigation">
        <Link to="/" aria-label="Guided Sentry home"><img src="/figma/brand.svg" alt="Guided Sentry" /></Link>
        <div className="fx-nav-actions">
          <Link to="/login" className="fx-button fx-button-outline">Admin Login</Link>
          <button type="button" className="fx-button fx-button-solid" onClick={onBookDemo}>Book a Demo</button>
        </div>
      </nav>
      <div className="fx-hero-copy">
        <h1>Plan your cloud<br />before you build it</h1>
        <p>Guided Sentry generates a complete cloud architecture from a description of what you&apos;re building — with cost estimates, provider comparisons, and IaC export. No blank canvas. No guessing.</p>
        <div className="fx-actions"><button type="button" className="fx-button fx-button-outline" onClick={onEarlyAccess}>Early access</button><a href="#how-it-works" className="fx-button fx-button-solid">See how it works</a></div>
      </div>
      <img
        className="fx-hero-product"
        src="/figma/hero-product.png?v=4x"
        alt="Guided Sentry cloud-planning interface"
      />
    </section>

    <section className="fx-main" id="how-it-works">
      <FigmaCapabilitySlider />
      <div className="fx-glow" aria-hidden="true" />
      <div className="fx-container">
        <header className="fx-workflow-heading"><h2>From idea to architecture<br />in minutes.</h2></header>
        <div className="fx-workflow">
          {EXPORTED_STEPS.map((step, index) => (
            <article className={`fx-workflow-row ${index === 1 ? "fx-workflow-row-reverse" : ""}`} key={step.number}>
              <div className="fx-step-copy"><h3>{step.title}</h3><p>{step.description}</p><div><span>STEP {step.number}</span><small>{step.label}</small></div></div>
              <img src={step.image} alt={`${step.title} illustration`} />
            </article>
          ))}
        </div>
        <section className="fx-capabilities" aria-labelledby="capabilities-heading">
          <header><p>CAPABILITIES</p><h2 id="capabilities-heading">Everything you need to plan with confidence</h2></header>
          <div className="fx-capability-grid">{EXPORTED_CAPABILITIES.map(([number, title, description]) => <article key={number}><span>{number}</span><h3>{title}</h3><p>{description}</p></article>)}</div>
        </section>
        <section className="fx-cta" id="contact" aria-labelledby="demo-heading"><p>READY TO PLAN</p><h2 id="demo-heading">See how Guided Sentry works</h2><div>Get a guided walkthrough of the product and explore a clearer way to plan your cloud architecture.</div><section><button type="button" className="fx-button fx-button-solid" onClick={onBookDemo}>Book a Demo</button><button type="button" className="fx-button fx-button-outline" onClick={onEarlyAccess}>Early access</button></section></section>
      </div>
    </section>
    <footer className="fx-footer"><div className="fx-footer-content"><div><strong>Guided Sentry</strong><span>Plan your cloud before you build it.</span></div><nav aria-label="Footer navigation"><a href="#product">Product</a><a href="#how-it-works">How it works</a><a href="#contact" onClick={(event) => { event.preventDefault(); onContact(); }}>Contact</a><Link to="/privacy">Privacy Policy</Link><Link to="/terms">Terms of Service</Link><Link to="/company/security">Security</Link></nav></div></footer>
  </main>
);

export default function Landing() {
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  return <><FigmaLanding onEarlyAccess={() => setEarlyAccessOpen(true)} onBookDemo={() => setDemoOpen(true)} onContact={() => setContactOpen(true)} /><EarlyAccessDialog open={earlyAccessOpen} onClose={() => setEarlyAccessOpen(false)} /><LeadDialog kind="demo" open={demoOpen} onClose={() => setDemoOpen(false)} /><LeadDialog kind="contact" open={contactOpen} onClose={() => setContactOpen(false)} /></>;
}
